/** Отдельный экземпляр axios: все запросы раздела идут только через него и только в мобильный бэкенд */

import axios, { AxiosError, AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';

import {
  MM_HTTP_TIMEOUT_MS,
  MM_RETRY_ATTEMPTS,
  MM_RETRY_BASE_DELAY_MS,
  MM_ENV_VAR_BACKEND_URL,
  getMobileBackendBaseUrl,
  isMobileBackendConfigured,
} from '../config/env';
import { createMisconfiguredError, isCanceled, isRetryableCode, toMmError, MmError } from '../core/errors';
import { createRequestId } from '../core/requestId';
import { getRawAccessToken } from '../core/tokenStore';

export interface MmRequestConfig extends AxiosRequestConfig {
  mmSkipAuth?: boolean;
  mmRetryCount?: number;
  mmReauthTried?: boolean;
  mmSection?: string;
  /** Нужен, чтобы отличить протухший свой токен от уже обновлённого параллельным запросом */
  mmTokenUsed?: string | null;
  mmNoRetry?: boolean;
}

export interface MmAxiosError extends AxiosError {
  mmError?: MmError;
}

/** Через регистрацию, а не импорт: прямой импорт замкнул бы цикл `mmHttp` — `session` — `auth` */
type ReauthHandler = () => Promise<boolean>;

type ConfirmTokenHandler = (tokenUsed: string | null) => void;

let reauthHandler: ReauthHandler | null = null;
let confirmTokenHandler: ConfirmTokenHandler | null = null;

export function registerReauthHandler(handler: ReauthHandler): void {
  reauthHandler = handler;
}

export function registerConfirmTokenHandler(handler: ConfirmTokenHandler): void {
  confirmTokenHandler = handler;
}

let reauthPromise: Promise<boolean> | null = null;

function runReauthSingleFlight(): Promise<boolean> {
  if (reauthPromise !== null) {
    return reauthPromise;
  }

  if (reauthHandler === null) {
    return Promise.resolve(false);
  }

  reauthPromise = reauthHandler()
    .catch(() => false)
    .then((result) => {
      reauthPromise = null;

      return result;
    });

  return reauthPromise;
}

/** Вызывать при смене торговой сети: состояние повторного входа привязано к сети */
export function resetReauthState(): void {
  reauthPromise = null;
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

/** Повторяем только идемпотентные методы: повтор POST создал бы вторую запись; PUT задаёт итог */
const MM_RETRY_SAFE_METHODS = ['get', 'head', 'options', 'put'];

function isMethodSafeToRetry(config: MmRequestConfig): boolean {
  const method = config && config.method ? String(config.method).toLowerCase() : '';

  return MM_RETRY_SAFE_METHODS.indexOf(method) >= 0;
}

/** Снимаем baseURL: axios уже вклеил его в config.url, при повторе префикс удвоился бы */
function prepareRetryConfig(config: MmRequestConfig): MmRequestConfig {
  config.baseURL = undefined;

  return config;
}

function createInstance(): AxiosInstance {
  const instance = axios.create({
    baseURL: getMobileBackendBaseUrl(),
    timeout: MM_HTTP_TIMEOUT_MS,
    headers: {
      Accept: 'application/json',
    },
  });

  instance.interceptors.request.use(
    (config: MmRequestConfig) => {
      /** Без заданного адреса запрос ушёл бы относительным путём в чужой бэкенд, поэтому не отправляем */
      if (!isMobileBackendConfigured()) {
        return Promise.reject(createMisconfiguredError(MM_ENV_VAR_BACKEND_URL));
      }

      config.headers = config.headers ? config.headers : {};
      config.headers['X-Request-Id'] = createRequestId();

      if (config.mmSkipAuth === true) {
        // Обмен сессии: мобильного токена ещё нет, SP-сессия уходит в теле.
        if (config.headers && config.headers.Authorization) {
          delete config.headers.Authorization;
        }

        config.mmTokenUsed = null;

        return config;
      }

      const token = getRawAccessToken();

      // Запоминаем именно тот токен, с которым уходит запрос: он нужен в обработке 401
      config.mmTokenUsed = token;

      if (token !== null) {
        config.headers = config.headers ? config.headers : {};
        config.headers.Authorization = 'Bearer ' + token;
      } else if (config.headers && config.headers.Authorization) {
        delete config.headers.Authorization;
      }

      return config;
    },
    (error) => Promise.reject(error),
  );

  instance.interceptors.response.use(
    (response: AxiosResponse) => {
      const cfg = (response && response.config ? response.config : null) as MmRequestConfig | null;

      if (
        cfg !== null &&
        cfg.mmSkipAuth !== true &&
        confirmTokenHandler !== null
      ) {
        confirmTokenHandler(cfg.mmTokenUsed ? cfg.mmTokenUsed : null);
      }

      return response;
    },
    (error: MmAxiosError) => {
      // Отменённый запрос пробрасываем как есть, чтобы у вызывающего работал isCancel()
      if (isCanceled(error)) {
        return Promise.reject(error);
      }

      const config = (error && error.config ? error.config : null) as MmRequestConfig | null;
      const status = error && error.response ? error.response.status : null;

      if (
        status === 401 &&
        config !== null &&
        config.mmSkipAuth !== true &&
        config.mmReauthTried !== true
      ) {
        config.mmReauthTried = true;

        /** Токен мог обновиться, пока запрос летел: без проверки каждый 401 запускал бы новый обмен */
        const currentToken = getRawAccessToken();

        if (currentToken !== null && currentToken !== config.mmTokenUsed) {
          // Новый токен этим запросом ещё не проверялся, поэтому признак повтора сбрасывается
          config.mmReauthTried = false;

          return instance.request(prepareRetryConfig(config));
        }

        return runReauthSingleFlight().then((refreshed) => {
          if (refreshed) {
            config.mmReauthTried = false;

            return instance.request(prepareRetryConfig(config));
          }

          error.mmError = toMmError(error, config.mmSection);

          return Promise.reject(error);
        });
      }

      const classified = toMmError(error, config !== null ? config.mmSection : undefined);

      if (
        config !== null
        && config.mmNoRetry !== true
        && isMethodSafeToRetry(config)
        && isRetryableCode(classified.code)
      ) {
        const attempt = config.mmRetryCount === undefined ? 0 : config.mmRetryCount;

        if (attempt < MM_RETRY_ATTEMPTS) {
          config.mmRetryCount = attempt + 1;

          return delay(MM_RETRY_BASE_DELAY_MS * (attempt + 1)).then(() =>
            instance.request(prepareRetryConfig(config)),
          );
        }
      }

      error.mmError = classified;

      return Promise.reject(error);
    },
  );

  return instance;
}

export const mmHttp: AxiosInstance = createInstance();

export function readMmError(error: any, sectionHint?: string): MmError {
  if (error && error.mmError) {
    return error.mmError as MmError;
  }

  return toMmError(error, sectionHint);
}

export function createCancelSource() {
  return axios.CancelToken.source();
}
